/**
 * Búsqueda de abstracts en Europe PMC (incluye todo PubMed; sin API key).
 *
 * https://europepmc.org/RestfulWebService
 */

import { decodeHTML } from "entities";

import { type Paper, type SearchPage, type SortOrder, paperUrl } from "../core/models";
import { type FetchFn, timing } from "./config";

// Solo etiquetas reales: no debe comerse comparaciones como "p < 0.05 ... > 25"
export const TAG_RE = /<\/?[a-zA-Z][a-zA-Z0-9]*(\s[^<>]*)?\/?>/g;

/** Quita el HTML de Europe PMC (<h4>, <i>…, a veces escapado como &lt;i&gt;). */
export function stripHtml(raw: string): string {
  // Atajo: sin entidades ni etiquetas solo hay que normalizar espacios (ahorra CPU)
  if (!/[&<]/.test(raw)) return raw.replace(/[ \t]+/g, " ").trim();
  const text = decodeHTML(raw).replace(/<h4>(.*?)<\/h4>/g, "\n$1: ");
  return decodeHTML(text.replace(TAG_RE, ""))
    .replace(/[ \t]+/g, " ")
    .trim();
}

/** Por defecto Europe PMC busca también en el texto completo, lo que mete mucho ruido. */
export function buildQuery(query: string, titleAbsOnly: boolean): string {
  return titleAbsOnly ? `TITLE_ABS:(${query})` : query;
}

interface EpmcResult {
  id?: string;
  source?: string;
  title?: string;
  abstractText?: string;
  doi?: string;
  pmid?: string;
  pmcid?: string;
  pubYear?: string | number;
  authorString?: string;
  journalInfo?: { journal?: { title?: string } };
  pubTypeList?: { pubType?: string[] };
  citedByCount?: number;
  meshHeadingList?: { meshHeading?: { descriptorName?: string }[] };
  keywordList?: { keyword?: string[] };
  isOpenAccess?: string;
  license?: string;
  fullTextUrlList?: { fullTextUrl?: { availability?: string; documentStyle?: string; url?: string }[] };
}

/** Texto completo gratuito: el de Europe PMC si es de acceso abierto; si no, un enlace libre. */
function openAccessUrl(r: EpmcResult): string | null {
  if (r.isOpenAccess === "Y" && r.pmcid) return `https://europepmc.org/article/PMC/${r.pmcid}`;
  const free = (r.fullTextUrlList?.fullTextUrl ?? []).filter((u) => /open access|^free$/i.test(u.availability ?? "") && u.url);
  return (free.find((u) => u.documentStyle === "html") ?? free[0])?.url ?? null;
}

export function parseResult(r: EpmcResult): Paper | null {
  if (!r.abstractText || !r.title) return null;
  const year = String(r.pubYear ?? "");
  const paper: Paper = {
    source_id: `${r.source ?? "?"}:${r.id}`,
    source: "europepmc",
    cited_by_count: r.citedByCount ?? null,
    title: stripHtml(r.title),
    abstract: stripHtml(r.abstractText),
    doi: r.doi ?? null,
    pmid: r.pmid ?? null,
    pmcid: r.pmcid ?? null,
    year: /^\d+$/.test(year) ? Number(year) : null,
    journal: r.journalInfo?.journal?.title ?? null,
    authors: r.authorString ?? null,
    publication_types: r.pubTypeList?.pubType ?? [],
    oa_url: openAccessUrl(r),
    license: r.license ?? null,
    keywords: [
      ...(r.meshHeadingList?.meshHeading ?? []).map((m) => m.descriptorName).filter((x): x is string => !!x),
      ...(r.keywordList?.keyword ?? []),
    ],
  };
  paper.url = paperUrl(paper);
  return paper;
}

export class SearchError extends Error {}

interface EpmcResponse {
  hitCount?: number;
  nextCursorMark?: string;
  resultList?: { result?: EpmcResult[] };
}

/**
 * Pide una página, reintentando fallos transitorios.
 *
 * Europe PMC a veces responde 200 con un cuerpo vacío (`{"version": "6.9"}`),
 * sin `hitCount` ni resultados; hay que tratarlo como error y no como "0 papers".
 */
async function getPage(fetchFn: FetchFn, url: string, params: URLSearchParams, retries: number) {
  let problem = "";
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (attempt) await timing.sleep(500 * 2 ** (attempt - 1));
    let resp: Response;
    try {
      resp = await fetchFn(`${url}?${params}`);
    } catch (e) {
      problem = `error de red: ${e}`;
      continue;
    }
    if (resp.status === 429 || resp.status >= 500) {
      problem = `HTTP ${resp.status}`;
      continue;
    }
    const text = await resp.text();
    if (!resp.ok) throw new SearchError(`Europe PMC devolvió HTTP ${resp.status}: ${text.slice(0, 200)}`);
    let data: EpmcResponse;
    try {
      data = JSON.parse(text);
    } catch {
      problem = "respuesta no JSON";
      continue;
    }
    if (data.hitCount === undefined) {
      problem = `respuesta incompleta: ${text.slice(0, 100)}`;
      continue;
    }
    return data;
  }
  throw new SearchError(`Europe PMC no respondió bien tras ${retries + 1} intentos (${problem})`);
}

export interface PageRequest {
  query: string;
  sort: SortOrder;
  cursor?: string;
  pageSize: number;
  titleAbsOnly: boolean;
}

/**
 * Una página de resultados. `sort: "default"` es el orden de Europe PMC (relevancia,
 * muy sesgada a lo reciente); `"cited"` trae primero los más citados.
 */
export async function searchPage(
  fetchFn: FetchFn,
  url: string,
  req: PageRequest,
  retries = 4,
): Promise<SearchPage> {
  const cursor = req.cursor || "*";
  const params = new URLSearchParams({
    query: buildQuery(req.query, req.titleAbsOnly),
    resultType: "core",
    format: "json",
    // La expansión de sinónimos mete ruido (p. ej. milk -> lactation)
    synonym: "false",
    pageSize: String(req.pageSize),
    cursorMark: cursor,
  });
  if (req.sort === "cited") params.set("sort", "CITED desc");
  const data = await getPage(fetchFn, url, params, retries);
  const results = data.resultList?.result ?? [];
  const next = data.nextCursorMark;
  return {
    papers: results.map(parseResult).filter((p): p is Paper => p !== null),
    hit_count: data.hitCount ?? 0,
    next_cursor: results.length && next && next !== cursor ? next : null,
  };
}
